import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import { Issue as IssueType, HighlightArticle } from '../types';
import { unescapeHtml } from '../utils/unescapeHtml';
import { sanitizeLimitedHtml } from '../utils/sanitizeLimitedHtml';
import SEOPage from '../components/SEOPage';
import seoGlobals from '../seo_globals.json';

const Issue: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [mag, setMag] = useState<IssueType | null>(null);
  const [highlights, setHighlights] = useState<HighlightArticle[]>([]);

  useEffect(() => {
    const load = async () => {
      let issue: IssueType | null = null;
      if (slug) {
        issue = await VanityAPI.getMagazineBySlug(slug);
      } else {
        issue = await VanityAPI.getCurrentMagazine();
      }
      setMag(issue);

      if (issue) {
        VanityAPI.getHighlights(issue.id).then(setHighlights);
      }
    };
    load();
  }, [slug]);

  if (!mag) return (
    <div className="h-screen flex flex-col items-center justify-center bg-vanity-white">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-vanity-pink mb-6"></div>
      <div className="font-serif italic text-2xl text-vanity-charcoal animate-pulse">Loading Issue...</div>
    </div>
  );

  return (
    <>
      <SEOPage
        title={mag.seoTitle || `${mag.title} | VANITY`}
        description={mag.seoDescription || seoGlobals.seo.defaultDescription}
        image={mag.coverUrl}
      />
      <div className="pt-32 pb-48 fade-in bg-vanity-white overflow-x-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-start mb-20">
            {/* Main Cover Section */}
            <div className="w-full max-w-[425px] md:max-w-[500px] mx-auto lg:mx-0 relative flex-shrink-0">
              <div>
                <div className="absolute -inset-4 bg-vanity-pink blur opacity-5"></div>
                <img
                  src={mag.coverUrl}
                  alt={mag.coverAltText || mag.title || "VANITY Magazine Anthology"}
                  className="w-full shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] relative z-10 block object-cover bg-vanity-charcoal/5"
                />
                <div className="mt-12 flex justify-between items-center text-[11px] uppercase tracking-[0.5em] font-extrabold text-vanity-pink">
                  <span>{mag.periodText}</span>
                  <span className="bg-vanity-black text-white px-5 py-2">№ {mag.issueDate}</span>
                </div>
                {mag.pdfUrl && (
                  <div className="mt-8 space-y-4 relative z-10">
                    <button
                      onClick={() => {
                        window.open(`/viewer/${mag.id}`, '_blank');
                      }}
                      className="flex items-center justify-center gap-4 w-full bg-vanity-pink text-white py-5 font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-vanity-black transition-all group cursor-pointer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
                      <span>PDF DURCHBLÄTTERN</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Editorial Content */}
            <div className="flex-1 space-y-24 w-full">
              <div className="space-y-8">
                <span className="font-sans text-[12px] uppercase tracking-[0.6em] text-vanity-grey font-bold">The Editorial Narrative</span>
                <h1 className="font-serif text-5xl md:text-7xl text-vanity-black leading-[0.9]">
                  {mag.title.split(' ').map((word, i) => <React.Fragment key={i}>{word} <br /></React.Fragment>)}
                </h1>
              </div>

              <div
                className="font-serif italic text-3xl md:text-4xl text-vanity-black/80 leading-snug max-w-2xl border-l-8 border-vanity-gold pl-12"
                dangerouslySetInnerHTML={{
                  __html: `"${sanitizeLimitedHtml(unescapeHtml(mag.summary))}"`
                }}
              />

              <div className="vanity-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeLimitedHtml(unescapeHtml(mag.editorialText)) || ""
                  }}
                />
              </div>

              {/* Dynamic Spot Columns - Only show if content exists */}
              {(mag.col1Title || mag.col1Text || mag.col2Title || mag.col2Text) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 border-t border-vanity-charcoal/10 pt-20">
                  {(mag.col1Title || mag.col1Text) && (
                    <div className="space-y-6">
                      {mag.col1Title && <h4 className="font-serif text-3xl text-vanity-black italic">{mag.col1Title}</h4>}
                      {mag.col1Text && (
                        <p className="font-sans text-base text-vanity-grey leading-relaxed">
                          {mag.col1Text}
                        </p>
                      )}
                    </div>
                  )}
                  {(mag.col2Title || mag.col2Text) && (
                    <div className="space-y-6">
                      {mag.col2Title && <h4 className="font-serif text-3xl text-vanity-black italic">{mag.col2Title}</h4>}
                      {mag.col2Text && (
                        <p className="font-sans text-base text-vanity-grey leading-relaxed">
                          {mag.col2Text}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Highlights Section - Full Width */}
          {highlights.length > 0 && (
            <section className="py-24 bg-vanity-charcoal/5 -mx-6 md:-mx-12 px-6 md:px-12 mb-20"> {/* Full bleed adjustments */}
              <div className="max-w-[1440px] mx-auto mb-16 flex justify-between items-end">
                <div>
                  <h3 className="font-sans text-[11px] uppercase tracking-[0.4em] text-vanity-grey mb-4 block">Inside VANITY</h3>
                  <h2 className="font-serif text-4xl md:text-5xl">Ausgewählte Highlights</h2>
                </div>
              </div>
              <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                {highlights.map((h, i) => (
                  <Link to={`/highlight/${h.slug}`} key={h.id} className="group cursor-pointer block">
                    <div className="aspect-[3/4] overflow-hidden mb-6 shadow-lg relative">
                      {h.imageUrl && (
                        <img
                          src={h.imageUrl}
                          alt={h.heroAltText || h.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-all duration-1000"
                        />
                      )}
                      <div className="absolute inset-0 bg-vanity-black/15 transition-opacity duration-700 group-hover:opacity-0"></div>
                      <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 font-sans text-[9px] uppercase tracking-widest z-10">
                        {h.category || 'Feature'}
                      </div>
                    </div>
                    <h4 className="font-serif text-2xl mb-3 group-hover:text-vanity-pink transition-colors">{h.title}</h4>
                    <div
                      className="font-sans text-sm text-vanity-grey-dark leading-relaxed line-clamp-3"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeLimitedHtml(unescapeHtml(h.text))
                      }}
                    />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Visual Spread Gallery - Full Width */}
          {mag.spreads && mag.spreads.length > 0 && (
            <section>
              <div className="mb-24 space-y-6 border-b border-vanity-charcoal/10 pb-16">
                <span className="font-sans text-[11px] uppercase tracking-[0.6em] text-vanity-pink font-extrabold">In-Depth Exploration</span>
                <h2 className="font-serif text-5xl md:text-7xl">Editorial Spreads</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
                {mag.spreads.map((spread, idx) => (
                  <div key={idx} className="group overflow-hidden shadow-3xl bg-vanity-charcoal/5 aspect-[3/4] relative">
                    <img
                      src={spread}
                      alt={`Feature Selection ${idx}`}
                      className="w-full h-full object-cover transition-transform duration-[2.5s] group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-vanity-pink/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default Issue;
